import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../services/api';
import { Button, Modal, FormField } from '../components/ui';
import CustomFieldsSection from '../components/CustomFieldsSection';
import { TAB_ICONS } from '../utils/tabIcons';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import trLocale from '@fullcalendar/core/locales/tr';

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Planlandı', color: '#3b82f6' },
  { value: 'completed', label: 'Tamamlandı', color: '#16a34a' },
  { value: 'cancelled', label: 'İptal', color: '#6b7280' },
  { value: 'no_show',   label: 'Gelmedi',   color: '#dc2626' },
];

const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s.label]));
const STATUS_COLOR = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s.color]));

// Personel başına deterministic renk paleti — id hash'inden index alır
const PERSONNEL_PALETTE = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
];
function hashStr(s) {
  let h = 0;
  if (!s) return 0;
  for (let i = 0; i < s.length; i++) h = ((h * 31) + s.charCodeAt(i)) >>> 0;
  return h;
}
function personnelColor(pid) {
  if (!pid) return null;
  return PERSONNEL_PALETTE[hashStr(String(pid)) % PERSONNEL_PALETTE.length];
}

const FREQ_OPTIONS = [
  { value: '',        label: 'Tekrar yok' },
  { value: 'DAILY',   label: 'Her gün' },
  { value: 'WEEKLY',  label: 'Her hafta' },
  { value: 'MONTHLY', label: 'Her ay' },
  { value: 'YEARLY',  label: 'Her yıl' },
];

const REMINDER_OPTIONS = [
  { value: '',     label: 'Yok' },
  { value: '0',    label: 'Tam zamanında' },
  { value: '5',    label: '5 dk önce' },
  { value: '10',   label: '10 dk önce' },
  { value: '15',   label: '15 dk önce' },
  { value: '30',   label: '30 dk önce' },
  { value: '60',   label: '1 saat önce' },
  { value: '120',  label: '2 saat önce' },
  { value: '1440', label: '1 gün önce' },
];

function buildRrule(freq, count) {
  if (!freq) return '';
  const parts = ['FREQ=' + freq];
  const c = parseInt(count, 10);
  if (Number.isFinite(c) && c > 1) parts.push('COUNT=' + c);
  return parts.join(';');
}

function parseRrule(s) {
  if (!s) return { freq: '', count: '' };
  const map = {};
  String(s).split(';').forEach(p => {
    const [k, v] = p.split('=');
    if (k) map[k.toUpperCase()] = (v || '').toUpperCase();
  });
  return { freq: map.FREQ || '', count: map.COUNT || '' };
}

const emptyForm = {
  title: '', description: '', location: '',
  start_at: '', end_at: '', all_day: false,
  account_id: '', personnel_id: '',
  status: 'scheduled', color: '', notes: '',
  freq: '', count: '', reminder_minutes_before: '', reminder_email: false,
};

function toLocalInput(d) {
  if (!d) return '';
  const dt = (d instanceof Date) ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function toIsoFromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

// FullCalendar duration string: HH:MM
function durationStr(startIso, endIso) {
  const s = new Date(startIso).getTime();
  const e = new Date(endIso).getTime();
  const mins = Math.max(1, Math.round((e - s) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
}

export default function AppointmentsPage() {
  const calendarRef = useRef(null);
  const [list, setList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterPersonnel, setFilterPersonnel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [range, setRange] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    api.get('/accounts', { params: { limit: 500 } })
      .then(r => { if (r.data.success) setAccounts((r.data.data.items || []).filter(a => a.is_active)); })
      .catch(() => {});
    api.get('/personnel', { params: { limit: 200 } })
      .then(r => { if (r.data.success) setPersonnel((r.data.data.items || []).filter(p => p.is_active)); })
      .catch(() => {});
  }, []);

  const fetchList = useCallback(async () => {
    if (!range) return;
    setLoading(true);
    try {
      const params = { from: range.from, to: range.to };
      if (filterPersonnel) params.personnel = filterPersonnel;
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      const r = await api.get('/appointments', { params });
      if (r.data.success) setList(r.data.data.items || []);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, [range, filterPersonnel, filterStatus, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const events = useMemo(() => list.map(a => {
    // Renk önceliği: kullanıcı override > personel rengi > status rengi
    const c = a.color || personnelColor(a.personnel_id) || STATUS_COLOR[a.status] || '#3b82f6';
    const base = {
      id: a.id,
      title: a.title,
      allDay: !!a.all_day,
      backgroundColor: c,
      borderColor: c,
      extendedProps: { raw: a },
    };
    if (a.rrule) {
      // Recurring — rrule plugin kullanılır; drag-drop devre dışı.
      return {
        ...base,
        rrule: { dtstart: a.start_at, ...parseRruleForFC(a.rrule) },
        duration: durationStr(a.start_at, a.end_at),
        editable: false,
      };
    }
    return { ...base, start: a.start_at, end: a.end_at };
  }), [list]);

  const tf = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const openAdd = useCallback((startDt, endDt, allDay) => {
    const s = startDt || new Date();
    const e = endDt || new Date(s.getTime() + 60 * 60 * 1000);
    setForm({
      ...emptyForm,
      start_at: toLocalInput(s),
      end_at: toLocalInput(e),
      all_day: !!allDay,
    });
    setSelected(null);
    setMsg(null);
    setModalMode('add');
  }, []);

  const openView = useCallback((a) => {
    const { freq, count } = parseRrule(a.rrule);
    setForm({
      title: a.title || '',
      description: a.description || '',
      location: a.location || '',
      start_at: toLocalInput(a.start_at),
      end_at: toLocalInput(a.end_at),
      all_day: !!a.all_day,
      account_id: a.account_id || '',
      personnel_id: a.personnel_id || '',
      status: a.status || 'scheduled',
      color: a.color || '',
      notes: a.notes || '',
      freq, count,
      reminder_minutes_before: a.reminder_minutes_before != null ? String(a.reminder_minutes_before) : '',
      reminder_email: !!a.reminder_email,
    });
    setSelected(a);
    setMsg(null);
    setModalMode('view');
  }, []);

  const closeModal = () => { setModalMode(null); setSelected(null); setMsg(null); };

  const handleSave = async () => {
    if (!form.title.trim()) return setMsg({ type: 'error', text: 'Başlık zorunlu' });
    const startIso = toIsoFromLocalInput(form.start_at);
    const endIso = toIsoFromLocalInput(form.end_at);
    if (!startIso || !endIso) return setMsg({ type: 'error', text: 'Başlangıç ve bitiş tarihi gerekli' });
    if (new Date(endIso) <= new Date(startIso)) return setMsg({ type: 'error', text: 'Bitiş başlangıçtan sonra olmalı' });
    setSaving(true); setMsg(null);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description || null,
        location: form.location || null,
        start_at: startIso,
        end_at: endIso,
        all_day: !!form.all_day,
        account_id: form.account_id || null,
        personnel_id: form.personnel_id || null,
        status: form.status,
        color: form.color || null,
        notes: form.notes || null,
        rrule: buildRrule(form.freq, form.count) || null,
        reminder_minutes_before: form.reminder_minutes_before === '' ? null : parseInt(form.reminder_minutes_before, 10),
        reminder_email: !!form.reminder_email,
      };
      if (modalMode === 'edit') await api.put('/appointments/' + selected.id, body);
      else await api.post('/appointments', body);
      closeModal();
      await fetchList();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm('Bu randevuyu silmek istiyor musunuz?')) return;
    try {
      await api.delete('/appointments/' + selected.id);
      closeModal();
      fetchList();
    } catch (err) {
      alert(err.response?.data?.message || 'Silinemedi');
    }
  };

  const handleEventChange = useCallback(async (info) => {
    const ev = info.event;
    // Recurring (rrule) randevular taşınamaz
    if (ev.extendedProps?.raw?.rrule) {
      info.revert();
      alert('Tekrarlı randevular sürüklenemez. Düzenlemek için randevuya tıklayın.');
      return;
    }
    try {
      await api.patch('/appointments/' + ev.id + '/move', {
        start_at: ev.start ? ev.start.toISOString() : null,
        end_at: (ev.end || ev.start) ? (ev.end || ev.start).toISOString() : null,
        all_day: !!ev.allDay,
      });
      setList(prev => prev.map(a => a.id === ev.id
        ? { ...a, start_at: ev.start, end_at: ev.end || ev.start, all_day: !!ev.allDay }
        : a
      ));
    } catch (err) {
      info.revert();
      alert(err.response?.data?.message || 'Taşınamadı');
    }
  }, []);

  return (
    <div className="stocks-page" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="stocks-topbar">
        <div className="stocks-topbar-left">
          <h2>Randevu / Ajanda</h2>
          <span className="stocks-count-badge">{list.length} randevu</span>
        </div>
        <div className="stocks-topbar-right" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Ara..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
          />
          <select
            value={filterPersonnel}
            onChange={e => setFilterPersonnel(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
          >
            <option value="">Tüm Personel</option>
            {personnel.map(p => (
              <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
          >
            <option value="">Tüm Durumlar</option>
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <Button size="sm" onClick={() => openAdd()}>+ Yeni Randevu</Button>
        </div>
      </div>

      {/* Personel renk göstergesi */}
      {personnel.length > 0 && (
        <div style={{ padding: '6px 16px', display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: '0.74rem', color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-secondary)' }}>
          <span style={{ fontWeight: 600 }}>Personel renkleri:</span>
          {personnel.map(p => (
            <span key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: personnelColor(p.id), display: 'inline-block' }}></span>
              {p.first_name} {p.last_name}
            </span>
          ))}
        </div>
      )}

      <div style={{ flex: 1, padding: '12px 16px', overflow: 'auto', minHeight: 500 }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, rrulePlugin]}
          initialView="dayGridMonth"
          locale={trLocale}
          headerToolbar={
            typeof window !== 'undefined' && window.innerWidth <= 768
              ? {
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                }
              : {
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                }
          }
          titleFormat={{ year: 'numeric', month: 'short' }}
          views={{
            dayGridMonth: { titleFormat: { year: 'numeric', month: 'short' } },
            timeGridWeek: { titleFormat: { year: 'numeric', month: 'short', day: 'numeric' }, dayHeaderFormat: { weekday: 'short', day: '2-digit' } },
            timeGridDay: { titleFormat: { year: 'numeric', month: 'short', day: 'numeric' }, dayHeaderFormat: { weekday: 'long' } },
            listWeek: { titleFormat: { year: 'numeric', month: 'short', day: 'numeric' } },
          }}
          buttonText={{ today: 'Bugün', month: 'Ay', week: 'Hafta', day: 'Gün', list: 'Liste' }}
          height="auto"
          firstDay={1}
          nowIndicator={true}
          editable={true}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={true}
          slotMinTime="07:00:00"
          slotMaxTime="22:00:00"
          weekNumbers={false}
          events={events}
          datesSet={(arg) => {
            const from = arg.start.toISOString();
            const to = arg.end.toISOString();
            setRange(prev => (prev?.from === from && prev?.to === to) ? prev : { from, to });
          }}
          select={(arg) => openAdd(arg.start, arg.end, arg.allDay)}
          eventClick={(arg) => {
            const a = arg.event.extendedProps.raw;
            if (a) openView(a);
          }}
          eventDrop={handleEventChange}
          eventResize={handleEventChange}
          eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
        />
        {loading && <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>}
      </div>

      {modalMode !== null && (() => {
        const ro = modalMode === 'view';
        const isNew = modalMode === 'add';
        return (
          <Modal
            icon={TAB_ICONS.settings}
            title={modalMode === 'add' ? 'Yeni Randevu' : (modalMode === 'edit' ? 'Randevu Düzenle' : (form.title || 'Randevu Bilgisi'))}
            subtitle={isNew ? null : (
              <>
                {form.start_at && (
                  <span>
                    {new Date(form.start_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                )}
                {form.status && (
                  <>
                    <span> · </span>
                    <span style={{padding:'1px 6px',borderRadius:4,background:(STATUS_COLOR[form.status]||'#64748b')+'22',color:STATUS_COLOR[form.status]||'#64748b',fontSize:'var(--text-xs)',fontWeight:600}}>
                      {STATUS_LABEL[form.status] || form.status}
                    </span>
                  </>
                )}
              </>
            )}
            size="lg"
            onClose={closeModal}
            destructiveAction={ro ? { label: 'Sil', onClick: handleDelete } : null}
            cancelAction={!ro ? { label: isNew ? 'Vazgeç' : 'İptal', onClick: modalMode === 'edit' ? () => setModalMode('view') : closeModal } : null}
            primaryAction={
              !ro ? { label: 'Kaydet', onClick: handleSave, loading: saving }
              : { label: 'Düzenle', onClick: () => { setMsg(null); setModalMode('edit'); }, variant: 'ghost' }
            }
          >
            {msg && (
              <div style={{
                marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
                background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
                color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
                border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
              }}>{msg.text}</div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <FormField label="Başlık" required={!ro} fullWidth>
                <input
                  disabled={ro}
                  value={form.title}
                  onChange={e => tf('title', e.target.value)}
                  maxLength={200}
                  placeholder="örn. Müşteri ziyareti — ABC Şirket"
                />
              </FormField>

              <FormField label="Müşteri (Cari)">
                <select disabled={ro} value={form.account_id} onChange={e => tf('account_id', e.target.value)}>
                  <option value="">— Seçiniz —</option>
                  {accounts.map(a => (
                    <option key={a.id} value={a.id}>{a.code} — {a.title}</option>
                  ))}
                </select>
              </FormField>

              <FormField label="Personel">
                <select disabled={ro} value={form.personnel_id} onChange={e => tf('personnel_id', e.target.value)}>
                  <option value="">— Seçiniz —</option>
                  {personnel.map(p => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                  ))}
                </select>
              </FormField>

              <FormField label="Başlangıç" required={!ro}>
                <input
                  type="datetime-local"
                  disabled={ro}
                  value={form.start_at}
                  onChange={e => tf('start_at', e.target.value)}
                />
              </FormField>

              <FormField label="Bitiş" required={!ro}>
                <input
                  type="datetime-local"
                  disabled={ro}
                  value={form.end_at}
                  onChange={e => tf('end_at', e.target.value)}
                />
              </FormField>

              <FormField label="Tüm Gün">
                <select disabled={ro} value={form.all_day ? '1' : '0'} onChange={e => tf('all_day', e.target.value === '1')}>
                  <option value="0">Hayır</option>
                  <option value="1">Evet</option>
                </select>
              </FormField>

              <FormField label="Durum">
                <select disabled={ro} value={form.status} onChange={e => tf('status', e.target.value)}>
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </FormField>

              <FormField label="Konum">
                <input
                  disabled={ro}
                  value={form.location}
                  onChange={e => tf('location', e.target.value)}
                  maxLength={200}
                  placeholder="örn. Müşteri ofisi, Ankara"
                />
              </FormField>

              <FormField label="Renk (boş = otomatik)">
                <input
                  type="color"
                  disabled={ro}
                  value={form.color || personnelColor(form.personnel_id) || STATUS_COLOR[form.status] || '#3b82f6'}
                  onChange={e => tf('color', e.target.value)}
                  style={{ height: 36, padding: 2 }}
                />
              </FormField>

              <FormField label="Tekrar">
                <select disabled={ro} value={form.freq} onChange={e => tf('freq', e.target.value)}>
                  {FREQ_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </FormField>

              <FormField label="Toplam Sayı (boş = sınırsız)">
                <input
                  type="number"
                  min={1}
                  disabled={ro || !form.freq}
                  value={form.count}
                  onChange={e => tf('count', e.target.value)}
                  placeholder={form.freq ? 'örn. 10' : '—'}
                />
              </FormField>

              <FormField label="Hatırlatma">
                <select disabled={ro} value={form.reminder_minutes_before} onChange={e => tf('reminder_minutes_before', e.target.value)}>
                  {REMINDER_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </FormField>

              <FormField label="E-posta da gönder">
                <select
                  disabled={ro || !form.reminder_minutes_before}
                  value={form.reminder_email ? '1' : '0'}
                  onChange={e => tf('reminder_email', e.target.value === '1')}
                >
                  <option value="0">Hayır</option>
                  <option value="1">Evet (atanan personele)</option>
                </select>
              </FormField>

              <FormField label="Açıklama" fullWidth>
                <textarea
                  rows={3}
                  disabled={ro}
                  value={form.description}
                  onChange={e => tf('description', e.target.value)}
                />
              </FormField>

              <FormField label="Notlar" fullWidth>
                <textarea
                  rows={2}
                  disabled={ro}
                  value={form.notes}
                  onChange={e => tf('notes', e.target.value)}
                  maxLength={500}
                />
              </FormField>
            </div>

            {ro && selected && (
              <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', borderTop: '1px solid var(--border-secondary)', paddingTop: 10, marginTop: 12 }}>
                Durum: <strong>{STATUS_LABEL[selected.status] || selected.status}</strong>
                {selected.account_title && <> · Müşteri: <strong>{selected.account_title}</strong></>}
                {(selected.personnel_first_name || selected.personnel_last_name) && (
                  <> · Personel: <strong>{(selected.personnel_first_name || '') + ' ' + (selected.personnel_last_name || '')}</strong></>
                )}
                {selected.rrule && <> · <strong>Tekrarlı</strong></>}
              </div>
            )}

            {/* ÖZEL ALANLAR — yalnızca kaydedilmiş randevu için */}
            {selected?.id && (
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border-secondary)' }}>
                <CustomFieldsSection entityType="appointment" entityId={selected.id} autoSave disabled={ro || modalMode !== 'edit'} />
              </div>
            )}
          </Modal>
        );
      })()}
    </div>
  );
}

// FullCalendar rrule plugin için string'i parse et
function parseRruleForFC(s) {
  const { freq, count } = parseRrule(s);
  const out = {};
  if (freq) out.freq = freq.toLowerCase();
  if (count) out.count = parseInt(count, 10);
  return out;
}
